/** COS 短时直传凭证；仅允许在当前上传调用内使用，不进入持久化状态。 */
export interface CosDirectUploadCredential {
  secretId: string;
  secretKey: string;
  sessionToken: string;
  expiredAt: string;
  /** 直传目标桶（服务端凭证下发，缺失时回退构建期 VITE_COS_* 兜底配置） */
  bucket?: string;
  /** 直传目标地域（与 bucket 配对） */
  region?: string;
}

/** 服务端签发的对象上传指令。 */
export interface CosDirectUploadInstruction {
  credentials: CosDirectUploadCredential;
  objectKey: string;
  directUpload: boolean;
}

/**
 * 按短时凭证直传对象到 COS。
 * fake 模式 directUpload=false 时跳过真实 PUT；real 模式才动态加载 SDK。
 * 桶信息以服务端凭证下发为准（与签发侧同源），VITE_COS_BUCKET/VITE_COS_REGION 仅作兜底。
 */
export async function uploadObjectToCos(
  instruction: CosDirectUploadInstruction,
  file: File,
): Promise<void> {
  if (!instruction.directUpload) return;

  const bucket = instruction.credentials.bucket ?? (import.meta.env.VITE_COS_BUCKET as string | undefined);
  const region = instruction.credentials.region ?? (import.meta.env.VITE_COS_REGION as string | undefined);
  if (!bucket || !region) {
    throw new Error('直传缺少 COS 桶信息（服务端凭证未下发 bucket/region 且未配置 VITE_COS_BUCKET / VITE_COS_REGION）');
  }

  const { default: COS } = await import('cos-js-sdk-v5');
  const cos = new COS({
    getAuthorization(_options, callback) {
      callback({
        TmpSecretId: instruction.credentials.secretId,
        TmpSecretKey: instruction.credentials.secretKey,
        SecurityToken: instruction.credentials.sessionToken,
        StartTime: Math.floor(Date.now() / 1000) - 60,
        ExpiredTime: Math.floor(new Date(instruction.credentials.expiredAt).getTime() / 1000),
      });
    },
  });

  await new Promise<void>((resolve, reject) => {
    cos.putObject(
      {
        Bucket: bucket,
        Region: region,
        Key: instruction.objectKey,
        Body: file,
      },
      (err) => (err ? reject(err) : resolve()),
    );
  });
}
